import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import './VehicleDetails.css';

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const canEdit = isAdmin();
  const [vehicleProfile, setVehicleProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    tag: '',
    make: '',
    model: '',
    park_number: '',
    downloaded: false
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchVehicleProfile();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVehicleProfile = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch vehicle profile details from backend
      const response = await apiClient.getVehicleProfiles();
      
      if (response.vehicle_profiles && Array.isArray(response.vehicle_profiles)) {
        // Find the vehicle profile by ID
        const vehicleData = response.vehicle_profiles.find(vp => {
          const profile = vp.profile || {};
          const profileId = profile.id || profile.VehicleProfileId;
          return String(profileId) === String(id) || profile.id === id;
        });

        if (vehicleData) {
          const profile = vehicleData.profile || {};
          const profileData = {
            id: profile.id || profile.VehicleProfileId,
            tag: profile.plate_number || profile.id,
            make: profile.Make,
            model: profile.Model,
            park_number: profile.Most_recent_park_number,
            downloaded: profile.Download_status === true,
            pictures: vehicleData.pictures || [],
            // Add other profile fields as needed
          };
          setVehicleProfile(profileData);
          // Initialize form data with current profile data
          setFormData({
            tag: profileData.tag || '',
            make: profileData.make || '',
            model: profileData.model || '',
            park_number: profileData.park_number || '',
            downloaded: profileData.downloaded || false
          });
        } else {
          setError('Vehicle profile not found');
        }
      }
    } catch (err) {
      console.error('Error fetching vehicle profile:', err);
      setError(err.message || 'Failed to load vehicle profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    if (name === 'tag') return;
    setFormData((previous) => ({ ...previous, [name]: inputValue }));
  };

  const handleUpdate = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setError(null);

    try {
      // Call API to update vehicle profile
      const response = await apiClient.patchVehicleProfile(vehicleProfile.id, {
        make: formData.make.trim() || null,
        model: formData.model.trim() || null,
        Most_recent_park_number: formData.park_number.trim() || null,
        download_status: formData.downloaded,
      });
      
      if (response && response.profile) {
        if (response.profile.id !== vehicleProfile.id) throw new Error('The server returned a changed profile ID; reload before proceeding.');
        // Update local state with new data
        const updatedProfile = response.profile;
        setVehicleProfile(prev => ({
          ...prev,
          tag: vehicleProfile.tag,
          make: updatedProfile.Make || formData.make,
          model: updatedProfile.Model || formData.model,
          park_number: updatedProfile.Most_recent_park_number || formData.park_number,
          downloaded: updatedProfile.Download_status === true
        }));
        
        // Exit edit mode
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error updating vehicle profile:', err);
      setError('Failed to update vehicle profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original values
    setFormData({
      tag: vehicleProfile.tag || '',
      make: vehicleProfile.make || '',
      model: vehicleProfile.model || '',
      park_number: vehicleProfile.park_number || '',
      downloaded: vehicleProfile.downloaded || false
    });
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="vehicle-details-page">
        <div className="main-title">Vehicle Profile Details</div>
        <div className="loading">Loading vehicle profile...</div>
      </div>
    );
  }

  if (error || !vehicleProfile) {
    return (
      <div className="vehicle-details-page">
        <div className="main-title">Vehicle Profile Details</div>
        <div className="error-message">{error || 'Vehicle profile not found'}</div>
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="vehicle-details-page">
      <div className="details-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
          <div className="main-title">Vehicle Profile Details</div>
        </div>
        {canEdit && <div className="header-right">
          {!isEditing ? (
            <button className="edit-button" onClick={() => setIsEditing(true)}>
              ✎ Edit
            </button>
          ) : (
            <div className="edit-actions">
              <button className="cancel-button" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </button>
              <button className="update-button" onClick={handleUpdate} disabled={isSaving}>
                {isSaving ? 'Updating...' : 'Update'}
              </button>
            </div>
          )}
        </div>}
      </div>
      
      <div className="details-content">
        <div className="details-card">
          <h2 className="card-title">Profile Information</h2>
          
          <div className="details-grid">
            <div className="detail-item">
              <label className="detail-label" htmlFor="vehicle-tag">Plate Number / Tag</label>
              {isEditing ? (
                <input
                  type="text"
                  id="vehicle-tag"
                  name="tag"
                  className="detail-input"
                  value={formData.tag}
                  readOnly
                />
              ) : (
                <div className="detail-value">{vehicleProfile.tag}</div>
              )}
            </div>

            <div className="detail-item">
              <label className="detail-label" htmlFor="vehicle-make">Make</label>
              {isEditing ? (
                <input
                  type="text"
                  id="vehicle-make"
                  name="make"
                  className="detail-input"
                  value={formData.make}
                  onChange={handleInputChange}
                />
              ) : (
                <div className="detail-value">{vehicleProfile.make || 'N/A'}</div>
              )}
            </div>

            <div className="detail-item">
              <label className="detail-label" htmlFor="vehicle-model">Model</label>
              {isEditing ? (
                <input
                  type="text"
                  id="vehicle-model"
                  name="model"
                  className="detail-input"
                  value={formData.model}
                  onChange={handleInputChange}
                />
              ) : (
                <div className="detail-value">{vehicleProfile.model || 'N/A'}</div>
              )}
            </div>

            <div className="detail-item">
              <label className="detail-label" htmlFor="vehicle-park-number">Park Number</label>
              {isEditing ? (
                <input
                  type="text"
                  id="vehicle-park-number"
                  name="park_number"
                  className="detail-input"
                  value={formData.park_number}
                  onChange={handleInputChange}
                />
              ) : (
                <div className="detail-value">{vehicleProfile.park_number || 'N/A'}</div>
              )}
            </div>

            <div className="detail-item">
              <label className="detail-label" htmlFor="vehicle-download-status">Download Status</label>
              {isEditing ? (
                <select
                  id="vehicle-download-status"
                  name="downloaded"
                  className="detail-select"
                  value={formData.downloaded}
                  onChange={(e) => setFormData(prev => ({ ...prev, downloaded: e.target.value === 'true' }))}
                >
                  <option value="false">○ Not Downloaded</option>
                  <option value="true">✓ Downloaded</option>
                </select>
              ) : (
                <div className="detail-value">
                  <span className={`status-badge ${vehicleProfile.downloaded ? 'downloaded' : 'not-downloaded'}`}>
                    {vehicleProfile.downloaded ? '✓ Downloaded' : '○ Not Downloaded'}
                  </span>
                </div>
              )}
            </div>

            <div className="detail-item">
              <label className="detail-label">Total Pictures</label>
              <div className="detail-value">{vehicleProfile.pictures.length}</div>
            </div>
          </div>
        </div>

        {vehicleProfile.pictures.length > 0 && (
          <div className="details-card">
            <h2 className="card-title">Associated Pictures</h2>
            <div className="pictures-grid">
              {vehicleProfile.pictures.map((picture, index) => (
                <div key={index} className="picture-thumbnail">
                  <img 
                    src={picture.sas_url || picture.pictureLink} 
                    alt={picture.id || `Picture ${index + 1}`}
                  />
                  <div className="picture-info">
                    <div className="picture-name">{picture.id}</div>
                    {picture.Shooting_time && (
                      <div className="picture-date">
                        {new Date(picture.Shooting_time).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleDetails;
